/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import { useReactiveVar } from '@apollo/client';
import * as React from 'react';
import { modalDataVar, ModalType } from '../../../apollo/reactiveVariables/TemplateManagementVariables';
import { LogCategory, ScenarioName, StatusName, StepName } from '../../../logger';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { DeployTemplate } from '../../components/DeployTemplate';
import { PreSetupTemplateWizard } from '../../components/PreSetupTemplateWizard';
import { TemplateInstanceIconPicker } from '../../components/TemplateInstanceIconPicker';
import { TemplateManagementModalComponent } from './TemplateManagementModal.component';

export const TemplateManagementModalContainer: React.FC = (): JSX.Element => {
    const modalData = useReactiveVar(modalDataVar);

    const onDismiss = React.useCallback((): void => {
        const logger = getAppHost().getLogger();
        if (modalData.type === ModalType.PreSetupWizard) {
            logger.logTraceInfo(LogCategory.PreSetupInstanceWizard, 'Closing the pre setup instance modal.');
            logger.markScenarioComplete(ScenarioName.CreateTemplateInstance);
            logger.markScenario(
                ScenarioName.TemplateManagement,
                StepName.ExitPreSetupTemplateInstanceModule,
                StatusName.NA
            );
        } else if (modalData.type === ModalType.PreviewTemplateInstance) {
            logger.logTraceInfo(LogCategory.PreSetupInstanceWizard, 'Closing the preview setup instance modal.');
            logger.markScenario(
                ScenarioName.CreateTemplateInstance,
                StepName.ExitPreviewTemplateInstance,
                StatusName.NA
            );
        } else if (modalData.type === ModalType.ChangeTemplateInstanceIcon) {
            logger.logTraceInfo(LogCategory.PreSetupInstanceWizard, 'Closing the template instance icon color picker.');
            logger.markScenario(ScenarioName.CreateTemplateInstance, StepName.ExitIconColorPicker, StatusName.NA);
        }

        modalDataVar({ isOpen: false });
    }, []);

    const onRenderContent = React.useCallback((): JSX.Element => {
        const logger = getAppHost().getLogger();
        if (modalData.type === ModalType.PreSetupWizard) {
            logger.logTraceInfo(LogCategory.PreSetupInstanceWizard, 'Enter pre setup template instance module.');
            return <PreSetupTemplateWizard template={modalData.template} />;
        } else if (modalData.type === ModalType.PreviewTemplateInstance) {
            logger.logTraceInfo(LogCategory.PreSetupInstanceWizard, 'Enter preview template instance module.');
            return <DeployTemplate />;
        } else if (modalData.type === ModalType.ChangeTemplateInstanceIcon) {
            logger.logTraceInfo(
                LogCategory.PreSetupInstanceWizard,
                'Enter template instance icon color picker module.'
            );
            return <TemplateInstanceIconPicker />;
        }
    }, [modalData]);

    return (
        <TemplateManagementModalComponent
            onRenderContent={onRenderContent}
            isOpen={modalData.isOpen}
            onDismiss={onDismiss}
        />
    );
};
